import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Sparkles, CheckCircle2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

/**
 * Modal that fetches AI-generated suggested actions from a task's GAP
 * analysis result and lets the user pick which ones to create as subtasks
 * under the current task.
 */
export default function AiSuggestSubtasksModal({ task, onClose, onCreated, t }) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('systemklart_token');
        const r = await axios.get(`${API}/gap-analysis/task-suggestions/${task.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuggestions(r.data);
        // Pre-select all new suggestions (not already created as subtasks)
        const existingTitles = new Set(
          (r.data.existing_subtasks || []).map(s =>
            (s.title || '').replace(/^\[GAP [^\]]+\]\s*/, '')
          )
        );
        const allIdx = new Set(
          (r.data.suggested_actions || [])
            .map((a, i) => existingTitles.has(a.title) ? null : i)
            .filter(i => i !== null)
        );
        setSelectedIdx(allIdx);
      } catch (e) {
        const msg = e.response?.data?.detail || t('tasks.aiSuggest.loadError');
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [task.id]);

  const toggle = (idx) => {
    setSelectedIdx(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const submit = async () => {
    if (selectedIdx.size === 0) {
      toast.error(t('tasks.aiSuggest.pickAtLeastOne'));
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const r = await axios.post(
        `${API}/gap-analysis/sessions/${suggestions.session_id}/create-tasks`,
        {
          result_ids: [suggestions.result_id],
          parent_task_id: task.id,
          action_indices: Array.from(selectedIdx),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('tasks.aiSuggest.created', { count: r.data.tasks_created }));
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('tasks.aiSuggest.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  const priorityColor = (p) => ({
    high: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    medium: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    low: 'bg-slate-600/20 text-slate-300 border-slate-600/30',
  }[p] || 'bg-slate-600/20 text-slate-300 border-slate-600/30');

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="ai-suggest-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-800 flex items-start gap-3">
          <Sparkles className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-white">{t('tasks.aiSuggest.title')}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{t('tasks.aiSuggest.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-white"
            data-testid="ai-suggest-close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 bg-slate-950/50">
          {loading ? (
            <div className="text-center py-10 text-slate-400">{t('common.loading')}...</div>
          ) : suggestions ? (
            <>
              <div className="mb-4 bg-slate-800/50 rounded-lg p-3 text-xs text-slate-300">
                <div className="font-semibold text-slate-200 mb-1">{suggestions.standard_name} §{suggestions.clause} — {suggestions.clause_title}</div>
                {suggestions.summary && <p className="text-slate-400">{suggestions.summary}</p>}
              </div>

              {suggestions.existing_subtasks?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-2">
                    {t('tasks.aiSuggest.existingSubtasks')} ({suggestions.existing_subtasks.length})
                  </p>
                  <ul className="space-y-1">
                    {suggestions.existing_subtasks.map(s => (
                      <li key={s.id} className="flex items-center gap-2 text-xs text-slate-400" data-testid={`existing-subtask-${s.id}`}>
                        {s.status === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Clock className="w-3.5 h-3.5 text-slate-500" />}
                        <span className="line-clamp-1">{s.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{t('tasks.aiSuggest.selectActions')}</p>
              <div className="space-y-2">
                {(suggestions.suggested_actions || []).map((a, i) => {
                  const active = selectedIdx.has(i);
                  return (
                    <label
                      key={i}
                      className={`block rounded-lg border p-3 cursor-pointer transition-colors ${
                        active ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      }`}
                      data-testid={`suggestion-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggle(i)}
                          className="mt-1 accent-cyan-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="text-sm font-medium text-slate-200">{a.title}</p>
                            <span className={`text-xs px-2 py-0.5 rounded border ${priorityColor(a.priority)}`}>
                              {t(`gap.${a.priority || 'medium'}`)}
                            </span>
                            {a.estimated_effort && (
                              <span className="text-xs text-slate-500">
                                · {t('gap.effort')}: {t(`gap.${a.estimated_effort}`)}
                              </span>
                            )}
                          </div>
                          {a.description && <p className="text-xs text-slate-400">{a.description}</p>}
                        </div>
                      </div>
                    </label>
                  );
                })}
                {(suggestions.suggested_actions || []).length === 0 && (
                  <div className="text-sm text-slate-400 italic">{t('tasks.aiSuggest.noActions')}</div>
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-3 bg-slate-900">
          <span className="text-xs text-slate-400">
            {selectedIdx.size} / {suggestions?.suggested_actions?.length || 0} {t('tasks.aiSuggest.selected')}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-600 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-800"
              data-testid="ai-suggest-cancel"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={submit}
              disabled={submitting || selectedIdx.size === 0 || loading}
              className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 disabled:opacity-40 shadow-lg shadow-cyan-500/25"
              data-testid="ai-suggest-submit"
            >
              {submitting ? t('common.loading') : t('tasks.aiSuggest.createBtn', { count: selectedIdx.size })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
